import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useEntity } from '@backstage/plugin-catalog-react';

import {
  evaluateEntity,
  evaluateEntityRuleCategories,
  NormalizedRule,
  RuleSeverity,
} from '../utils/maturityEvaluator';
import { rules } from '../rules/rules';
import { MaturityProgressBar } from './MaturityProgressBar';

type MissingRule = NormalizedRule & { category: string };

const severityOrder: Record<RuleSeverity, number> = {
  critical: 0,
  major: 1,
  minor: 2,
};

export const ComponentMaturitySummaryCard = () => {
  const { entity } = useEntity();
  const theme = useTheme();

  if (!entity || entity.kind?.toLocaleLowerCase('en-US') !== 'component') {
    return null;
  }

  const { passed, total, passedWeight, totalWeight } = evaluateEntity(entity, rules);
  const categoryEvaluations = evaluateEntityRuleCategories(entity, rules);

  const severityColors: Record<RuleSeverity, string> = {
    critical: theme.palette.error.main,
    major: theme.palette.warning.main,
    minor: theme.palette.text.secondary,
  };

  const severityLabels: Record<RuleSeverity, string> = {
    critical: 'Critical',
    major: 'Major',
    minor: 'Minor',
  };

  const missingChecks = useMemo(() => {
    const flattened: MissingRule[] = [];

    categoryEvaluations.forEach(category => {
      category.missingChecks.forEach(rule => {
        flattened.push({ ...rule, category: category.category });
      });
    });

    return flattened
      .sort((a, b) => {
        const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
        if (severityDiff !== 0) return severityDiff;
        if (b.weight !== a.weight) return b.weight - a.weight;
        if (a.category !== b.category) return a.category.localeCompare(b.category);
        return a.name.localeCompare(b.name);
      });
  }, [categoryEvaluations]);

  const topGaps = missingChecks.slice(0, 3);

  const kind = (entity.kind ?? 'Component').toLocaleLowerCase('en-US');
  const ns = entity.metadata.namespace ?? 'default';
  const name = entity.metadata.name ?? '';
  const maturityPath = `/catalog/${ns}/${kind}/${name}/maturity`;
  const manifestPath = `/catalog/${ns}/${kind}/${name}/manifest`;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Maturity Snapshot
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Weighted progress and the most important remaining fixes for this component.
        </Typography>

        <Box mt={2}>
          <MaturityProgressBar
            passed={passed}
            total={total}
            passedWeight={passedWeight}
            totalWeight={totalWeight}
          />
        </Box>

        {topGaps.length > 0 ? (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Top gaps
            </Typography>
            <List dense>
              {topGaps.map(rule => (
                <ListItem key={`${rule.category}-${rule.name}`} disableGutters>
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <HighlightOffIcon
                      fontSize="small"
                      style={{ color: severityColors[rule.severity] }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2' }}
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <span>{rule.name}</span>
                        <Typography
                          variant="caption"
                          style={{ color: severityColors[rule.severity] }}
                        >
                          {severityLabels[rule.severity]}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      rule.guidance ? (
                        <Typography variant="caption" color="textSecondary">
                          {rule.guidance}
                        </Typography>
                      ) : undefined
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            All checks complete—nice work!
          </Typography>
        )}

        <Box mt={2} display="flex" flexWrap="wrap" justifyContent="flex-end">
          <Button
            component="a"
            href={manifestPath}
            size="small"
            variant="outlined"
            style={{ marginRight: 8, marginBottom: 8 }}
          >
            Open Manifest Editor
          </Button>
          <Button
            component="a"
            href={maturityPath}
            size="small"
            color="primary"
            variant="contained"
            style={{ marginBottom: 8 }}
          >
            View maturity report
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
